import { Lightning, Colors, Router, AppData } from '@lightningjs/sdk';
import NavBarTab, { NavBarTabTemplateSpec } from 'components/widgets/NavBarTab';
import { PageId } from 'types/pageId';
import { translate } from 'support/translate';
import { ListWithSpeech } from 'components/common/CollectionWrappersWithSpeech';
import { constants } from 'aliases';
import { FromRoute, WidgetNames } from 'config/routes';
import {
  getRoutePathFromPageId,
  navigateToContentHub,
} from 'support/routerUtils';
import Page from 'components/Page';
import ContentHubPage from 'components/pages/content-hub/ContentHubPage';
import { ContentHubSlugs } from 'types/contentHubs';
import { reportNavBarNavigation } from 'services/analytics/reportingServicePage';
import { HoverableComponent } from 'components/common/HoverableComponent';
import { closeApp } from 'support/appUtils';
import { TOP_NAV_LONG_UP_PRESS_THRESHOLD_MS } from './TopNav';

export interface NavBarTemplateSpec extends Lightning.Component.TemplateSpec {
  Menu: typeof ListWithSpeech;
  RightGradient: Lightning.Element;
}

export const NAV_ANIMATION_DURATION = 0.5;

const NAVBAR_COLLAPSED_WIDTH = constants.sizing.navbar.collapsedWidth;
const NAVBAR_EXPANDED_WIDTH = constants.sizing.navbar.expandedWidth;

// Smaller gradient width -> sharper edge
const COLLAPSED_GRADIENT_WIDTH = NAVBAR_COLLAPSED_WIDTH / 4;
const EXPANDED_GRADIENT_WIDTH = NAVBAR_EXPANDED_WIDTH / 4;

const ICON_BASE_PATH = 'static/images/navigation/';

const LIGHT_ICON_EXTENSION = '-icon-inactive.svg';

const TAB_SPACING = 10;
const TAB_HEIGHT_COLLAPSED = 50;
const TAB_HEIGHT_EXPANDED = 70;
const TAB_HEIGHT_DIFF = TAB_HEIGHT_EXPANDED - TAB_HEIGHT_COLLAPSED;

const NAVBAR_MID_POINT = 1080 / 2;

export default class NavBar
  extends HoverableComponent<NavBarTemplateSpec>
  implements Lightning.Component.ImplementTemplateSpec<NavBarTemplateSpec>
{
  private _Menu = this.getByRef('Menu')!;
  private _RightGradient = this.getByRef('RightGradient')!;
  private _blockInteraction = false;
  private _upLongPressStartTime: number | null = null;

  private static _shouldUpdateTabs = false;

  private _tabs: Lightning.Element.PatchTemplate<NavBarTabTemplateSpec>[] = [];

  get announce() {
    return translate('ttsPrompts.navbar');
  }

  static updateTabs() {
    NavBar._shouldUpdateTabs = true;
  }

  static override _template(): Lightning.Component.Template<NavBarTemplateSpec> {
    return {
      rect: true,
      zIndex: constants.ui.zIndex.navLayer,
      w: NAVBAR_COLLAPSED_WIDTH - COLLAPSED_GRADIENT_WIDTH,
      h: 1080,
      color: Colors('background').alpha(0.35).get(),
      Menu: {
        type: ListWithSpeech,
        itemType: NavBarTab,
        direction: 'column',
        spacing: TAB_SPACING,
      },
      RightGradient: {
        rect: true,
        h: 1080,
        x: NAVBAR_COLLAPSED_WIDTH - COLLAPSED_GRADIENT_WIDTH,
        w: COLLAPSED_GRADIENT_WIDTH,
        color: Colors('background').alpha(0.35).get(),
        shader: {
          type: Lightning.shaders.FadeOut,
          right: COLLAPSED_GRADIENT_WIDTH,
        },
      },
    };
  }

  override _onActivated(page: Router.PageInstance) {
    if (NavBar._shouldUpdateTabs) this.updateNavBarTabs();
    this.updateSelectedTabFromPage(page as Page);
  }

  override _getFocused() {
    return this._Menu;
  }

  override _focus() {
    this.handleFocusUpdate(true);
  }

  override _unfocus() {
    this.handleFocusUpdate(false);
  }

  override _captureKey() {
    return this._blockInteraction;
  }

  override _handleBack() {
    AppData?.device.beforeAppClose(showPopUp => {
      if (showPopUp) AppData?.modalService.open('ExitModal');
      else closeApp();
    });
  }

  override _handleKey(e: KeyboardEvent) {
    switch (e.key) {
      case 'ArrowRight':
      case 'Escape':
        return false;
    }

    // When the handle key event propagates up, it causes the navbar to close. However,
    // we only want the navbar to close to specific key presses defined above. Otherwise,
    // we will stay focused on the navbar
    return true;
  }

  override _handleEnter() {
    this.handleItemSelected();
    return true;
  }

  override _handleHover(target: any) {
    if (
      this.alpha < 1 ||
      AppData?.device.hoverDisabled() ||
      this._blockInteraction ||
      // Workaround for preventing hovering when modals are "on top"
      AppData?.modalService.isOtherModalActive(this)
    )
      return true;

    Router.focusWidget('NavBar');
    return false;
  }

  override _handleUnhover() {
    if (
      this.alpha < 1 ||
      AppData?.device.hoverDisabled() ||
      this._blockInteraction ||
      AppData?.modalService.isOtherModalActive(this)
    )
      return true;
    AppData?.modalService.close();
    return false;
  }

  override _handleClick() {
    if (this.alpha < 1 || this._blockInteraction) return;
    if (!AppData?.device.canMouseClick()) return;

    this.handleItemSelected(true);
  }

  override _captureUp(): boolean | void {
    if (this._upLongPressStartTime) {
      const shouldFocusTopNav =
        Date.now() - this._upLongPressStartTime >
        TOP_NAV_LONG_UP_PRESS_THRESHOLD_MS;
      if (shouldFocusTopNav) {
        // Make top nav visible before focusing it
        const activePage = Router.getActivePage() as Page;
        if (
          activePage?.topNavFocusable &&
          activePage?.isEnabledWidget?.(WidgetNames.TOP_NAV)
        ) {
          activePage.topNavVisible = true;
          // Mark that we came from the navbar so TopNav knows to return here
          if (AppData) {
            AppData.topNavCameFromNavBar = true;
          }
          Router.focusWidget('TopNav');
        }
        this._upLongPressStartTime = null;
      }
      return true;
    }

    this._upLongPressStartTime = Date.now();
    return false;
  }

  override _captureUpRelease(): boolean | void {
    this._upLongPressStartTime = null;
    return false;
  }

  private updateNavBarTabs() {
    this._tabs = this.buildNavBarTabsUsingTabData();

    this._Menu.patch({ items: this._tabs });
    this.updateMenuPosition(false);
    NavBar._shouldUpdateTabs = false;
  }

  private updateMenuPosition(isActive: boolean) {
    const tabHeight = isActive ? TAB_HEIGHT_EXPANDED : TAB_HEIGHT_COLLAPSED;
    const menuHeight =
      this._tabs.length * tabHeight + (this._tabs.length - 1) * TAB_SPACING;

    const newY = NAVBAR_MID_POINT - menuHeight / 2;
    this._Menu.setSmooth('y', newY, { duration: NAV_ANIMATION_DURATION });
  }

  private buildNavBarTabsUsingTabData() {
    return AppData!
      .navigationItems!.map(item => {
        const { slug } = item;
        const navbarData = this.getTabDataFromSlug(slug);
        if (navbarData) return this.buildNavBarTab(navbarData);
      })
      .filter(
        tab => !!tab,
      ) as Lightning.Element.PatchTemplate<NavBarTabTemplateSpec>[];
  }

  private buildNavBarTab(props: {
    icon: string;
    slug: string;
    title: string;
  }): Lightning.Element.PatchTemplate<NavBarTabTemplateSpec> {
    return {
      w: NAVBAR_COLLAPSED_WIDTH,
      h: TAB_HEIGHT_COLLAPSED,
      icon: props.icon,
      slug: props.slug,
      title: props.title,
    };
  }

  private updateSelectedTabFromSlug(slug: string) {
    this._Menu.items.forEach((tab: NavBarTab, index: number) => {
      const selected = slug === tab.slug;

      tab.selected = selected;
      if (selected) this._Menu.index = index;
    });
  }

  private updateSelectedTabFromPage(page: Page) {
    const pageId = page.pageId;

    this._Menu.items.forEach((tab: NavBarTab, index: number) => {
      const { slug } = tab;
      const { pageId: tabPageId } = this.getTabDataFromSlug(slug)!;

      let selected = false;
      if (pageId === PageId.CONTENT_HUB) {
        selected = (page as ContentHubPage).hubSlug === slug;
      } else {
        selected = pageId === tabPageId;
      }

      tab.selected = selected;
      if (selected) this._Menu.index = index;
    });
  }

  private updateGradient(expanded: boolean) {
    if (!expanded) {
      this.patch({
        color: Colors('background').alpha(0.35).get(),
      });
      this._RightGradient.patch({
        w: COLLAPSED_GRADIENT_WIDTH,
        color: Colors('background').alpha(0.35).get(),
        shader: {
          type: Lightning.shaders.FadeOut,
          right: COLLAPSED_GRADIENT_WIDTH,
        },
      });
    } else {
      this.patch({
        colorLeft: Colors('background').alpha(1).get(),
        colorRight: Colors('background').alpha(0.85).get(),
      });
      this._RightGradient.patch({
        w: EXPANDED_GRADIENT_WIDTH,
        color: Colors('background').alpha(0.85).get(),
        shader: {
          type: Lightning.shaders.FadeOut,
          right: EXPANDED_GRADIENT_WIDTH,
        },
      });
    }
  }

  private handleFocusUpdate(shouldExpand: boolean) {
    const newWidth = shouldExpand
      ? NAVBAR_EXPANDED_WIDTH - EXPANDED_GRADIENT_WIDTH
      : NAVBAR_COLLAPSED_WIDTH - COLLAPSED_GRADIENT_WIDTH;

    if (this.w === newWidth) return;

    requestAnimationFrame(() => {
      this.updateGradient(shouldExpand);
      this._Menu.items.forEach((tab: NavBarTab, index: number) => {
        tab.showTitle = shouldExpand;
        tab.setSmooth('w', newWidth);

        tab.setSmooth(
          'h',
          shouldExpand ? TAB_HEIGHT_EXPANDED : TAB_HEIGHT_COLLAPSED,
          { duration: NAV_ANIMATION_DURATION },
        );
        // Update tab spacings on active
        tab.setSmooth('y', index * (shouldExpand ? TAB_HEIGHT_DIFF : 0), {
          duration: NAV_ANIMATION_DURATION,
        });

        this.updateMenuPosition(shouldExpand);
        tab.setActive(shouldExpand);

        if (index + 1 === this._Menu.items.length) {
          this.setSmooth('w', newWidth, {
            duration: NAV_ANIMATION_DURATION,
          });
          this._RightGradient.setSmooth('x', newWidth, {
            duration: NAV_ANIMATION_DURATION,
          });
        }
      });
    });
  }

  private handleItemSelected(click = false) {
    if (click && !this._Menu.currentItem.hovered) return;
    const { slug } = this._Menu.currentItem as NavBarTab;
    const { pageId, route, title } = this.getTabDataFromSlug(slug)!;
    const from = FromRoute.NAVBAR;
    this.updateSelectedTabFromSlug(slug);

    // Loading new page while the navBar animation is in progress cause visual stutter.
    // Early start unfocus animation first and then handle navigate once the animation is completed.
    this.transition('w').once('finish', () => {
      if (pageId === PageId.CONTENT_HUB) {
        navigateToContentHub(slug, { from });
      } else {
        Router.navigate(route, { from });
      }
      this._blockInteraction = false;
      reportNavBarNavigation(title);
    });

    this._blockInteraction = true;
    this.handleFocusUpdate(false);
  }

  private getTabDataFromSlug(slug: string) {
    const tabData = AppData!.navigationItems!.find(item => item.slug === slug);
    if (!tabData) return null;

    const pageId = this.getPageIdFromNavbarItemType(tabData.type);
    if (!pageId) return null;

    const title = tabData.title;
    const route = getRoutePathFromPageId(pageId, slug);
    const defaultIcons = this.getDefaultIconsFromNavbarSlug(slug);
    // We use this one icon and adjust the opacity to simulate active/inactive states.
    const icon = tabData.icons.svg_light ?? defaultIcons;
    return { icon, pageId, route, slug, title };
  }

  private getPageIdFromNavbarItemType(type: string): PageId | null {
    switch (type) {
      case 'channels':
        return PageId.EPG;
      case 'content-hub':
        return PageId.CONTENT_HUB;
      case 'search':
        return PageId.SEARCH;
      case 'my-cw':
        return PageId.MY_STUFF;
      default:
        return null;
    }
  }

  private getDefaultIconsFromNavbarSlug(slug: string) {
    switch (slug) {
      case 'channels':
        return this.buildDefaultIconPaths('channels');
      case 'search':
        return this.buildDefaultIconPaths('search');
      case ContentHubSlugs.HOME:
        return this.buildDefaultIconPaths('home');
      case ContentHubSlugs.MOVIES:
        return this.buildDefaultIconPaths('movies');
      case ContentHubSlugs.SERIES:
        return this.buildDefaultIconPaths('shows');
      case ContentHubSlugs.SPORTS:
        return this.buildDefaultIconPaths('sports');
      default:
        return this.buildDefaultIconPaths('special-event');
    }
  }

  private buildDefaultIconPaths(slug: string) {
    return `${ICON_BASE_PATH}${slug}${LIGHT_ICON_EXTENSION}`;
  }
}
